import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Filler,
  Legend,
  Tick,
  Plugin,
  TooltipItem,
  ScriptableScaleContext,
  Color,
  ChartData,
  ChartTypeRegistry,
  ScatterDataPoint,
  BubbleDataPoint
} from 'chart.js';
import { memo, useMemo } from 'react';
import { ChartProps, Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Colors } from '../../../../theme/colors';
import { useAreaChartStyles } from './area-chart.styles';
import { LoadingIndicator } from '../../loading-indicator/loading-indicator.component';
import { AnyObject } from 'chart.js/types/basic';
import { useMediaQuery, useTheme } from '@mui/material';

ChartJS.register(TimeScale, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend);

type AreaChartDataset = Omit<ChartProps<'line'>['data']['datasets'][number], 'data'> & {
  data: { x: number; y: number }[];
};

export enum AreaChartDateLabelUnit {
  Hour = 'hour',
  Day = 'day',
  Month = 'month'
}

export interface AreaChartProps {
  isLoading: boolean;
  data: {
    labels?: number[];
    datasets: AreaChartDataset[];
  };
  yAxisLabelCallback?(tickValue: string | number, index: number, ticks: Tick[]): string | number | null;
  yAxisLabels?: (string | number)[];
  yAxisMax?: number;
  maxDate?: number; // Date in milliseconds
  dateLabelUnit: AreaChartDateLabelUnit;
  tooltipLabelCallback?(tooltipItem: TooltipItem<'line'>): string;
  tooltipFilterCallback?(
    e: TooltipItem<'line'>,
    index: number,
    array: TooltipItem<'line'>[],
    data: ChartData<keyof ChartTypeRegistry, (number | ScatterDataPoint | BubbleDataPoint | null)[], unknown>
  ): boolean;
  yAxisGridColorCallback?(ctx: ScriptableScaleContext, options: AnyObject): Color | undefined;
  showXAxis?: boolean; // Control x-axis display
  xAxisTickSource?: 'labels' | 'auto'; // Control x-axis tick source, default 'labels'
}

export const AreaChart = memo(function AreaChart({
  isLoading,
  data,
  maxDate,
  dateLabelUnit,
  yAxisLabelCallback,
  yAxisLabels,
  yAxisMax,
  tooltipLabelCallback,
  tooltipFilterCallback,
  yAxisGridColorCallback,
  showXAxis = true,
  xAxisTickSource = 'labels'
}: AreaChartProps) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const classes = useAreaChartStyles();
  const options: ChartProps<'line'>['options'] = useMemo(
    () => ({
      animation: {
        easing: 'easeInOutQuart'
      },
      plugins: {
        tooltip: {
          intersect: false,
          filter: tooltipFilterCallback,
          callbacks: {
            label: tooltipLabelCallback
          }
        },
        legend: {
          display: false
        }
      },
      responsive: true,
      aspectRatio: isXs ? 0.7 : isSm ? 2.0 : 4.0,
      datasets: {
        line: {
          pointRadius: 0
        }
      },
      scales: {
        xAxis: {
          display: showXAxis,
          type: 'time',
          position: 'bottom',
          time: {
            unit: dateLabelUnit,
            displayFormats: {
              hour: 'ha',
              day: 'EEE',
              month: 'MMM'
            }
          },
          ticks: {
            source: xAxisTickSource,
            align: 'start',
            display: showXAxis,
            maxTicksLimit: xAxisTickSource === 'auto' ? 20 : undefined,
            font: {
              family: 'Poppins',
              lineHeight: 2
            },
            padding: 8,
            callback(tickValue, index, ticks) {
              if (index + 1 === ticks.length) {
                return '';
              }
              if (maxDate && index + 2 === ticks.length) {
                return '';
              }

              // Only filter labels when using 'auto' source to show labels only when day/month changes
              if (xAxisTickSource === 'auto' && index > 0) {
                const currentTick = ticks[index];
                const previousTick = ticks[index - 1];

                if (currentTick && previousTick) {
                  const currentDate = new Date(currentTick.value as number);
                  const previousDate = new Date(previousTick.value as number);

                  // Check if day changed
                  if (dateLabelUnit === AreaChartDateLabelUnit.Day || dateLabelUnit === AreaChartDateLabelUnit.Hour) {
                    if (
                      currentDate.getDate() === previousDate.getDate() &&
                      currentDate.getMonth() === previousDate.getMonth() &&
                      currentDate.getFullYear() === previousDate.getFullYear()
                    ) {
                      return '';
                    }
                  }

                  // Check if month changed
                  if (dateLabelUnit === AreaChartDateLabelUnit.Month) {
                    if (
                      currentDate.getMonth() === previousDate.getMonth() &&
                      currentDate.getFullYear() === previousDate.getFullYear()
                    ) {
                      return '';
                    }
                  }
                }
              }

              return tickValue;
            }
          },
          grid: {
            z: 100,
            lineWidth: 1,
            drawTicks: false,
            color(ctx) {
              return ctx.tick.value === maxDate ? Colors.bookedScannedInProgressGray : Colors.extraLightGray;
            }
          }
        },
        yAxis: {
          labels: yAxisLabels,
          suggestedMax: yAxisMax,
          suggestedMin: 0,
          grid: {
            display: true,
            color: yAxisGridColorCallback,
            tickLength: 30
          },
          ticks: {
            source: 'labels',
            stepSize: 0.5,
            callback: yAxisLabelCallback,
            padding: 0
          }
        }
      }
    }),
    [
      dateLabelUnit,
      isXs,
      isSm,
      maxDate,
      tooltipFilterCallback,
      tooltipLabelCallback,
      yAxisGridColorCallback,
      yAxisLabelCallback,
      yAxisLabels,
      yAxisMax,
      showXAxis,
      xAxisTickSource
    ]
  );

  const labels = data.labels || [];

  data.labels = maxDate ? [...labels.slice(0, labels.length - 1), maxDate, labels[labels.length - 1]] : labels;

  return (
    <div className={classes.wrapper}>
      <LoadingOverlay isLoading={isLoading} />
      <Line options={options} data={data} plugins={[VeticalLineOnHover]} />
    </div>
  );
});

const VeticalLineOnHover: Plugin<'line'> = {
  id: 'vertical-line-on-hover',
  afterDraw(chart) {
    if (chart.tooltip?.getActiveElements()?.length) {
      let x = chart.tooltip.getActiveElements()[0].element.x;
      let yAxis = chart.scales.yAxis;
      let ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(0, 0, 255, 0.4)';
      ctx.stroke();
      ctx.restore();
    }
  }
};

function LoadingOverlay({ isLoading }: { isLoading: boolean }) {
  const classes = useAreaChartStyles();
  if (!isLoading) {
    return null;
  }
  return (
    <div className={classes.loading}>
      <LoadingIndicator />
    </div>
  );
}
